import React, { useState } from "react";
import {
  useModalOldContext,
  WSButton,
  WSContainer,
  WSCentered,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import { RouteComponentProps, useHistory } from "react-router-dom";
import {
  useUpdateAccounts,
  useUpdateActivity
} from "../../../query/users/mutations";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { useUserId } from "../../../query/hooks/helpers";
import { ProjectOnboardingLayout } from "../../../components/ProjectOnboardingLayout/ProjectOnboardingLayout";
import { AccountStatus, IAccount } from "@wingspanhq/users/dist/lib/interfaces";
import {
  AccountsCheckList,
  AccountsCheckLoading
} from "../../components/AccountsCheckList/AccountsCheckList";
import {
  BOOKKEEPING_NO_ACCOUNTS_SELECTED,
  NoAccountsSelected
} from "../modals/NoAccountsSelected";
import { useAccounts } from "../../../query/users/queries";
import { WSQueries } from "../../../query/WSQuery";

export const updateForBookkeeping = async (
  accounts: IAccount[],
  updateAccounts: any
) => {
  if (accounts.some(a => a.usedFor?.bookkeeping)) {
    await updateAccounts(
      accounts.map(({ accountId, usedFor, status, events }) => ({
        accountId,
        usedFor: {
          ...(usedFor ? { bookkeeping: usedFor.bookkeeping } : {})
        },
        ...(status !== AccountStatus.Disconnected
          ? { status: AccountStatus.Active }
          : {}),
        ...(usedFor?.bookkeeping
          ? {}
          : {
              events: {
                ...events,
                initialSetupAt: new Date()
              }
            })
      }))
    );
  } else {
    await updateAccounts(
      accounts.map(({ accountId, usedFor, status }) => ({
        accountId,
        usedFor,
        ...(status !== AccountStatus.Disconnected
          ? { status: AccountStatus.Active }
          : {})
      }))
    );
  }
};

export const BookkeepingSetUpPlaidInstitutionSync: React.FC<
  RouteComponentProps<{
    institutionId: string;
  }>
> = ({ match }) => {
  const userId = useUserId();
  const history = useHistory();

  const { openModal } = useModalOldContext();

  const institutionId = match.params.institutionId;

  const [updateActivity] = useUpdateActivity(userId);
  const { updateAccounts, updateAccountsLoading } = useUpdateAccounts();

  const [accounts, setAccounts] = useState<IAccount[]>();

  const qAccounts = useAccounts();

  useBrowserPageTitle("Bookkeeping - Setup");

  return (
    <ProjectOnboardingLayout progress={(100 / 5) * 2} noBack>
      <NoAccountsSelected />
      <WSContainer verticalPadding>
        <WSCentered span={{ m: "6" }}>
          <WSText.ParagraphSm color="gray500" mt="M" pt="M">
            2 of 5
          </WSText.ParagraphSm>
          <WSText.Heading4>Sync ongoing business transactions</WSText.Heading4>

          <WSPanel mt="2XL">
            <WSText.Heading5 mt="XL">
              Which accounts contain business transactions?
            </WSText.Heading5>

            <WSText mt="XL">
              We make reviewing easier, so connect all applicable accounts.
            </WSText>

            <WSQueries
              queries={{ qAccounts }}
              renderLoader={() => <AccountsCheckLoading />}
            >
              {({ qAccounts }) => {
                const currentAccounts =
                  accounts ??
                  qAccounts.data.filter(
                    a =>
                      a.canBeUsedFor?.bookkeeping &&
                      a.institutionId === institutionId &&
                      a.status !== AccountStatus.Inactive
                  );

                const onSubmit = async (skipBusinessOnlyPage: boolean) => {
                  await updateForBookkeeping(currentAccounts, updateAccounts);

                  await updateActivity({
                    flows: {
                      bookkeepingSetup: {
                        version: 4,
                        currentStep: 3,
                        totalSteps: 6,
                        complete: false
                      }
                    }
                  });

                  if (skipBusinessOnlyPage) {
                    history.push("/member/bookkeeping/setup/accounts");
                  } else {
                    history.push(
                      `/member/bookkeeping/setup/import/plaid/${institutionId}/business-only`
                    );
                  }
                };

                return (
                  <>
                    <AccountsCheckList
                      data-testId="syncAccountsChecklist"
                      mt="XL"
                      accounts={currentAccounts}
                      onToggle={account => ({
                        ...account,
                        usedFor: {
                          ...account.usedFor,
                          bookkeeping: !account.usedFor?.bookkeeping
                        }
                      })}
                      onChange={setAccounts}
                      getValue={account => !!account.usedFor?.bookkeeping}
                    />
                    <WSButton.Primary
                      mt="2XL"
                      fullWidth
                      data-testId="continue"
                      loading={updateAccountsLoading}
                      onClick={async () => {
                        if (
                          currentAccounts.some(
                            account => !!account.usedFor?.bookkeeping
                          )
                        ) {
                          await onSubmit(false);
                        } else {
                          openModal(BOOKKEEPING_NO_ACCOUNTS_SELECTED, {
                            onContinue: () => onSubmit(true)
                          });
                        }
                      }}
                    >
                      Continue
                    </WSButton.Primary>
                  </>
                );
              }}
            </WSQueries>
          </WSPanel>
        </WSCentered>
      </WSContainer>
    </ProjectOnboardingLayout>
  );
};
